import { useId } from 'react';

/**
 * Around the World AI — network wireframe globe brand logo.
 *
 * Signature mark: wireframe Earth (meridians + latitudes) with network nodes
 * connected by lines across the surface, wrapped by a tilted orbital ring with
 * a magenta comet dot. Fully transparent, works on any background.
 *
 * Props:
 *  - size: pixel size of the icon square
 *  - showWordmark: display the "AroundTheWorld" wordmark next to the mark
 *  - showTagline: display the small "THE AI DEV STUDIO..." tagline (only usable with vertical variant)
 *  - variant: 'horizontal' | 'stacked'
 *  - tone: 'light' | 'dark' (text color)
 */
export default function BrandLogo({
  size = 32,
  showWordmark = true,
  showTagline = false,
  variant = 'horizontal',
  tone = 'light',
  className = '',
}) {
  const raw = useId();
  const uid = raw.replace(/[:]/g, 'x');
  const gridId = `atw-grid-${uid}`;
  const ringId = `atw-ring-${uid}`;
  const cometId = `atw-comet-${uid}`;
  const underlineId = `atw-under-${uid}`;

  const wordCls = tone === 'light' ? 'text-white' : 'text-slate-900';
  const tagCls = tone === 'light' ? 'text-white/55' : 'text-slate-500';

  // Base network globe SVG. Uses currentColor for grid so we can tint downstream.
  const svg = (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      aria-hidden="true"
      style={{ overflow: 'visible', color: tone === 'light' ? '#e2e8f0' : '#0f172a' }}
    >
      <defs>
        <linearGradient id={ringId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#00F0FF" stopOpacity="0" />
          <stop offset="50%" stopColor="#00F0FF" stopOpacity="1" />
          <stop offset="100%" stopColor="#FF007F" stopOpacity="0" />
        </linearGradient>
        <linearGradient id={underlineId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#00F0FF" />
          <stop offset="100%" stopColor="#FF007F" />
        </linearGradient>
        <radialGradient id={cometId} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#FF007F" />
          <stop offset="60%" stopColor="#FF007F" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#FF007F" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* --- Wireframe globe grid --- */}
      <g stroke="currentColor" strokeOpacity="0.55" strokeWidth="0.7" fill="none">
        {/* Equator + poles */}
        <circle cx="50" cy="50" r="26" />
        {/* Latitudes */}
        <ellipse cx="50" cy="50" rx="26" ry="10" />
        <ellipse cx="50" cy="50" rx="26" ry="18" strokeOpacity="0.35" />
        {/* Meridians */}
        <ellipse cx="50" cy="50" rx="10" ry="26" />
        <ellipse cx="50" cy="50" rx="18" ry="26" strokeOpacity="0.35" />
        {/* Diagonal meridian */}
        <path d="M 32 32 Q 50 20 68 32 Q 60 50 68 68 Q 50 80 32 68 Q 40 50 32 32 Z" strokeOpacity="0.2" />
      </g>

      {/* --- Network nodes on globe surface --- */}
      <g fill="#00F0FF">
        {/* Positioned like continents / hubs */}
        <circle cx="35" cy="38" r="1.8" />
        <circle cx="52" cy="30" r="1.6" />
        <circle cx="65" cy="42" r="2.0" />
        <circle cx="45" cy="58" r="1.6" />
        <circle cx="60" cy="65" r="1.8" />
        <circle cx="30" cy="55" r="1.5" />
      </g>

      {/* --- Network lines between nodes --- */}
      <g stroke="#00F0FF" strokeOpacity="0.75" strokeWidth="0.8" fill="none" strokeLinecap="round">
        <line x1="35" y1="38" x2="52" y2="30" />
        <line x1="52" y1="30" x2="65" y2="42" />
        <line x1="35" y1="38" x2="45" y2="58" />
        <line x1="65" y1="42" x2="60" y2="65" />
        <line x1="45" y1="58" x2="60" y2="65" />
        <line x1="30" y1="55" x2="45" y2="58" />
      </g>

      {/* --- Tilted orbital ring --- */}
      <g transform="rotate(-22 50 50)">
        <ellipse
          cx="50"
          cy="50"
          rx="46"
          ry="14"
          fill="none"
          stroke={`url(#${ringId})`}
          strokeWidth="1.4"
          strokeLinecap="round"
        />
        {/* Comet head */}
        <circle cx="6" cy="50" r="4" fill={`url(#${cometId})`} />
        <circle cx="6" cy="50" r="2" fill="#FF007F" />
      </g>
    </svg>
  );

  const wordmark = (
    <div className="flex flex-col leading-none">
      <div className="flex items-baseline">
        <span
          className={`font-display font-black tracking-[-0.02em] ${wordCls}`}
          style={{
            fontSize: variant === 'stacked' ? '1.4rem' : size >= 44 ? '1.25rem' : '1rem',
          }}
        >
          AroundTheWorld
        </span>
        <span
          className="ml-1 font-display font-black"
          style={{
            fontSize: variant === 'stacked' ? '1.05rem' : size >= 44 ? '1rem' : '0.85rem',
            background: 'linear-gradient(90deg, #00F0FF 0%, #FF007F 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          .AI
        </span>
      </div>
      {variant === 'stacked' && (
        <>
          {/* Cyan → magenta underline stripe */}
          <div
            className="mt-1.5 h-[2px] rounded-full"
            style={{
              background: `linear-gradient(90deg, #00F0FF 0%, #FF007F 100%)`,
              width: '85%',
              opacity: 0.9,
            }}
          />
          {showTagline && (
            <div
              className={`mt-2 font-mono uppercase tracking-[0.35em] text-[9px] ${tagCls}`}
            >
              The AI dev studio for founders who ship
            </div>
          )}
        </>
      )}
    </div>
  );

  if (variant === 'stacked') {
    return (
      <div className={`inline-flex flex-col items-center gap-3 ${className}`}>
        {svg}
        {showWordmark && wordmark}
      </div>
    );
  }
  return (
    <div className={`inline-flex items-center gap-2.5 ${className}`}>
      {svg}
      {showWordmark && wordmark}
    </div>
  );
}
